import { createEmptyPartyDrafts, normalizePartySlots } from '@/features/events/builder';
import type { GuildState } from '@/types/app-state';
import type {
  EventBuilderDraft,
  EventBuilderPartyDraft,
  EventBuilderSlotDraft,
  PartyTemplateSlot,
  TemplateBuilderDraft,
  TemplatePartyLabel,
} from '@/types/domain';

export interface TemplateSlotMetrics {
  configuredSlotCount: number;
  activeSlotCount: number;
  totalRequired: number;
  configuredPartyCount: number;
}

interface ComparableTemplateSlot {
  partyNumber: number;
  slotOrder: number;
  note: string;
  roleCategory: EventBuilderSlotDraft['roleCategory'];
  weaponId?: string;
  weaponName: string;
  imagePath: string;
  capacity: number;
  waitlistEnabled: boolean;
  isActive: boolean;
}

interface ComparableTemplateParty {
  partyNumber: number;
  label: string;
  sortOrder: number;
  slots: ComparableTemplateSlot[];
}

interface ComparableTemplateDraft {
  name: string;
  description: string;
  isActive: boolean;
  parties: ComparableTemplateParty[];
}

function createClientId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeSlotDraft(
  slot: Partial<EventBuilderSlotDraft>,
  partyNumber: number,
  slotOrder: number,
): EventBuilderSlotDraft {
  return {
    clientId: slot.clientId ?? createClientId(),
    id: slot.id,
    partyNumber,
    slotOrder,
    note: slot.note ?? '',
    roleCategory: slot.roleCategory ?? 'DPS',
    weaponId: slot.weaponId,
    weaponName: slot.weaponName ?? '',
    imagePath: slot.imagePath ?? '',
    capacity: Math.max(slot.capacity ?? 1, 1),
    waitlistEnabled: slot.waitlistEnabled ?? true,
    isActive: slot.isActive ?? true,
  };
}

function normalizePartyDraft(party: Partial<EventBuilderPartyDraft>, partyNumber: number): EventBuilderPartyDraft {
  const slots = Array.isArray(party.slots) ? party.slots : [];
  const normalizedSlots = slots
    .slice()
    .sort((left, right) => (left.slotOrder ?? 0) - (right.slotOrder ?? 0))
    .map((slot, index) => normalizeSlotDraft(slot, partyNumber, slot.slotOrder ?? index + 1));

  return {
    id: party.id,
    partyNumber,
    label: party.label?.trim() || `Party ${partyNumber}`,
    sortOrder: party.sortOrder ?? partyNumber,
    slots: normalizePartySlots(normalizedSlots),
  };
}

export function createDefaultTemplateDraft(): TemplateBuilderDraft {
  return {
    name: '',
    description: '',
    isActive: true,
    parties: createEmptyPartyDrafts(),
  };
}

export function normalizeTemplateBuilderDraft(draft: Partial<TemplateBuilderDraft>): TemplateBuilderDraft {
  const parties = Array.isArray(draft.parties) ? draft.parties : [];

  return {
    id: draft.id,
    name: draft.name ?? '',
    description: draft.description ?? '',
    isActive: draft.isActive ?? true,
    parties: createEmptyPartyDrafts().map((defaultParty) =>
      normalizePartyDraft(
        parties.find((party) => party.partyNumber === defaultParty.partyNumber) ?? defaultParty,
        defaultParty.partyNumber,
      ),
    ),
  };
}

export function createTemplatePartyLabels(parties: EventBuilderPartyDraft[]): TemplatePartyLabel[] {
  return createEmptyPartyDrafts().map((defaultParty) => {
    const party = parties.find((entry) => entry.partyNumber === defaultParty.partyNumber) ?? defaultParty;

    return {
      partyNumber: defaultParty.partyNumber,
      label: party.label.trim() || defaultParty.label,
      sortOrder: party.sortOrder ?? defaultParty.sortOrder,
    };
  });
}

export function getTemplateSlotMetrics(slots: PartyTemplateSlot[]): TemplateSlotMetrics {
  const activeSlots = slots.filter((slot) => slot.isActive);

  return {
    configuredSlotCount: slots.length,
    activeSlotCount: activeSlots.length,
    totalRequired: activeSlots.reduce((sum, slot) => sum + slot.capacity, 0),
    configuredPartyCount: new Set(slots.map((slot) => slot.partyNumber)).size,
  };
}

export function createTemplateComparableDraft(draft: TemplateBuilderDraft): ComparableTemplateDraft {
  const normalizedDraft = normalizeTemplateBuilderDraft(draft);

  return {
    name: normalizedDraft.name.trim(),
    description: normalizedDraft.description.trim(),
    isActive: normalizedDraft.isActive,
    parties: normalizedDraft.parties.map((party) => ({
      partyNumber: party.partyNumber,
      label: party.label.trim(),
      sortOrder: party.sortOrder,
      slots: party.slots.map((slot) => ({
        partyNumber: slot.partyNumber,
        slotOrder: slot.slotOrder,
        note: slot.note.trim(),
        roleCategory: slot.roleCategory,
        weaponId: slot.weaponId,
        weaponName: slot.weaponName.trim(),
        imagePath: slot.imagePath.trim(),
        capacity: slot.capacity,
        waitlistEnabled: slot.waitlistEnabled,
        isActive: slot.isActive,
      })),
    })),
  };
}

export function createTemplateDraftSignature(draft: TemplateBuilderDraft): string {
  return JSON.stringify(createTemplateComparableDraft(draft));
}

export function hasMeaningfulTemplateDraftContent(draft: TemplateBuilderDraft): boolean {
  const comparableDraft = createTemplateComparableDraft(draft);

  return Boolean(
    comparableDraft.name ||
      comparableDraft.description ||
      comparableDraft.parties.some((party) => party.slots.length > 0),
  );
}

export function cloneTemplateBuilderDraft(
  draft: TemplateBuilderDraft,
  options?: {
    asNew?: boolean;
    nextName?: string;
  },
): TemplateBuilderDraft {
  const normalizedDraft = normalizeTemplateBuilderDraft(draft);

  return {
    ...normalizedDraft,
    id: options?.asNew === false ? normalizedDraft.id : undefined,
    name: options?.nextName ?? normalizedDraft.name,
    parties: normalizedDraft.parties.map((party) => ({
      ...party,
      slots: party.slots.map((slot) => ({
        ...slot,
        id: options?.asNew === false ? slot.id : undefined,
        clientId: createClientId(),
      })),
    })),
  };
}

export function createTemplateDraftFromState(state: GuildState, templateId: string): TemplateBuilderDraft | null {
  const template = state.templates.find((entry) => entry.id === templateId);

  if (!template) {
    return null;
  }

  const parties = createEmptyPartyDrafts().map((party) => {
    const savedParty = template.partyLabels.find((entry) => entry.partyNumber === party.partyNumber);

    return {
      ...party,
      label: savedParty?.label ?? `Party ${party.partyNumber}`,
      sortOrder: savedParty?.sortOrder ?? party.sortOrder,
      slots: state.templateSlots
        .filter((slot) => slot.templateId === template.id && slot.partyNumber === party.partyNumber)
        .sort((left, right) => left.slotOrder - right.slotOrder)
        .map((slot) => ({
          clientId: createClientId(),
          id: slot.id,
          templateSlotId: slot.id,
          partyNumber: slot.partyNumber,
          slotOrder: slot.slotOrder,
          note: slot.note ?? (slot.roleName !== slot.roleCategory ? slot.roleName : ''),
          roleCategory: slot.roleCategory,
          weaponId: slot.weaponId,
          weaponName: slot.weaponName,
          imagePath: slot.imagePath,
          capacity: slot.capacity,
          waitlistEnabled: slot.waitlistEnabled,
          isActive: slot.isActive,
        })),
    };
  });

  return normalizeTemplateBuilderDraft({
    id: template.id,
    name: template.name,
    description: template.description,
    isActive: template.isActive,
    parties,
  });
}

export function createTemplateDraftFromMassDraft(draft: EventBuilderDraft): TemplateBuilderDraft {
  return normalizeTemplateBuilderDraft({
    name: draft.title.trim(),
    description: draft.description,
    isActive: true,
    parties: draft.parties.map((party) => ({
      ...party,
      slots: party.slots.map((slot) => ({
        clientId: createClientId(),
        templateSlotId: slot.templateSlotId,
        partyNumber: slot.partyNumber,
        slotOrder: slot.slotOrder,
        note: slot.note,
        roleCategory: slot.roleCategory,
        weaponId: slot.weaponId,
        weaponName: slot.weaponName,
        imagePath: slot.imagePath,
        capacity: slot.capacity,
        waitlistEnabled: slot.waitlistEnabled,
        isActive: slot.isActive,
      })),
    })),
  });
}
